<template>
  <v-col
    :class="classesForColumnResizer"
    :data-columnIndex="columnIndex"
  >
    <v-tooltip bottom>
      <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
        <button
          type="button"
          class="cursor-resizer"
          v-bind="tooltipAttrs"
          v-on="tooltipOn"
          @pointerdown.stop="onResizeStart"
          @dblclick.stop="onResetWidth"
        >
          <font-awesome-icon
            :icon="['fas', 'left-right']"
            size="xs"
            class="midgray--text"
          />
        </button>
      </template>

      <div>{{ normalizedColumnWidth }}px &bull; Double-click to reset</div>
    </v-tooltip>
  </v-col>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

const { mapActions, mapState } = createNamespacedHelpers('tools')

export default {
  name: 'ColumnResizer',
  props: {
    columnIndex: {
      type: Number,
      default: -1,
    },
    columnWidth: {
      type: [Number, String],
      default: 0,
    },
  },
  computed: {
    ...mapState({
      dragging: (state) => state.dragging,
    }),
    normalizedColumnWidth() {
      const parsedWidth = Number.parseFloat(this.columnWidth)

      const isFiniteWidth = () => Number.isFinite(parsedWidth)
      const isNaNWidth = () => Number.isNaN(parsedWidth)

      const isValidWidth = isFiniteWidth() && !isNaNWidth()

      return isValidWidth ? Math.round(parsedWidth * 100) / 100 : 0
    },
    classesForColumnResizer() {
      const draggingClass = this.dragging && this.dragging.index === this.columnIndex && ' column-dragging'
      return `col-2 pa-0 text-right column-resizer${draggingClass || ''}`
    },
  },
  methods: {
    ...mapActions(['startDragging', 'keepDragging', 'stopDragging', 'resetColumnWidth']),

    /**
     * Double-click: reset this column back to its configured default width.
     */
    onResetWidth() {
      this.resetColumnWidth({ columnIndex: this.columnIndex })
    },

    /**
     * Uses Pointer Capture so the resize continues tracking even when
     * the pointer moves outside the resizer element or the browser window.
     * Listeners are attached to the capture target and self-clean on pointerup.
     */
    onResizeStart(event) {
      const target = event.currentTarget
      if (target.setPointerCapture && Number.isFinite(event.pointerId)) {
        target.setPointerCapture(event.pointerId)
      }

      this.startDragging({ event, columnIndex: this.columnIndex })

      const onMove = (e) => this.keepDragging(e)
      const onEnd = (e) => {
        if (target.releasePointerCapture && Number.isFinite(e.pointerId)) {
          target.releasePointerCapture(e.pointerId)
        }
        target.removeEventListener('pointermove', onMove)
        target.removeEventListener('pointerup', onEnd)
        target.removeEventListener('pointercancel', onEnd)
        this.stopDragging(e)
      }

      target.addEventListener('pointermove', onMove)
      target.addEventListener('pointerup', onEnd)
      target.addEventListener('pointercancel', onEnd)
    },
  },
}
</script>

<style lang="scss" scoped>
@import "src/styles/tables";

.column-resizer {
  cursor: col-resize;
  touch-action: none; /* Prevent scroll while resizing on touch devices. */
}

.cursor-resizer {
  appearance: none;
  background: transparent;
  border: none;
  cursor: col-resize;
  width: 18px;
  height: 18px;
  padding: 0;
  margin: 0;
  border-radius: 3px;
  transition: background-color 0.15s ease, opacity 0.15s ease;
  opacity: 0.55;

  &:hover {
    background: rgba(0, 0, 0, 0.07);
    opacity: 1;
  }

  &:active {
    background: rgba(0, 0, 0, 0.12);
  }
}

.theme--dark .cursor-resizer {
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  &:active {
    background: rgba(255, 255, 255, 0.18);
  }
}

.column-dragging {
  opacity: 0.7;
}
</style>
